import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSport, SPORT_META } from "@/context/SportContext";
import { useProgressView } from "@/context/ProgressViewContext";
import SportSwitcher from "@/components/SportSwitcher";
import SkillSnapshotRadar from "@/components/SkillSnapshotRadar";
import StudentSpiderTimeline from "@/components/StudentSpiderTimeline";
import PurchaseHistory from "@/components/PurchaseHistory";
import ProgressGlance from "@/components/ProgressGlance";
import MilestonesRoadmap from "@/components/MilestonesRoadmap";
import SimpleRoadmap from "@/components/SimpleRoadmap";
import EditorialProgressMap from "@/components/EditorialProgressMap";
import EncouragementCard from "@/components/EncouragementCard";
import RadarExpandModal from "@/components/RadarExpandModal";
import PersonalPathwayPyramid from "@/components/PersonalPathwayPyramid";
import PerformanceAthleteCard from "@/components/PerformanceAthleteCard";
import AchievementBadges from "@/components/AchievementBadges";
import PlayerLevelCard from "@/components/PlayerLevelCard";
import { ProgressSummaryCard } from "@/components/ProgressSummaryCard";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import { Sparkles, Users, ChevronDown, ChevronRight, Maximize2, ListChecks, BarChart3 } from "lucide-react";

// Iter66: Simple/Advanced view toggle for the student progress page.
// Iter74: shared via ProgressViewContext so /dashboard and /progress agree
// and the choice persists to the user profile.

export default function MyProgress() {
  const { user } = useAuth();
  const { sport, skills, meta, multipleSports } = useSport();
  const { view, setView } = useProgressView();
  const [history, setHistory] = useState([]);
  const [synergy, setSynergy] = useState(null);
  const [byCoach, setByCoach] = useState(null);
  const [showOthers, setShowOthers] = useState(false);
  const [radarOpen, setRadarOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get(`/skills/${user.id}?sport=${sport}`).then((r) => setHistory(r.data));
    api.get(`/skills/${user.id}/by-coach?sport=${sport}`).then((r) => setByCoach(r.data));
    if (multipleSports) api.get(`/synergy/${user.id}`).then((r) => setSynergy(r.data));
  }, [user, sport, multipleSports]);

  const lineData = useMemo(() => history.map((h) => {
    const vals = Object.values(h.ratings || {}).filter((v) => typeof v === "number");
    return { date: (h.created_at || "").slice(5, 10), avg: vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : 0 };
  }), [history]);

  const multiSport = multipleSports && (synergy?.active_sports || []).length >= 2;

  return (
    <div className="space-y-6" data-testid="my-progress">
      <div className="flex items-end justify-between flex-wrap gap-4 fade-up">
        <div>
          <div className="label-mono mb-2">MY PROGRESS</div>
          <h1 className="h-display text-5xl tracking-tighter">Where you stand.</h1>
        </div>
        <SportSwitcher />
      </div>

      {sport === "pickleball" && (
        <>
          <PlayerLevelCard />
          <ProgressSummaryCard />
        </>
      )}

      {/* Simple / Advanced view toggle — big-text pill radio, shared with /dashboard */}
      <ViewToggle view={view} onChange={setView} />

      {view === "simple" ? (
        <>
          {/* Iter159 — Redesigned Simple view: glance-first. Shows a hero
              card with current spider + latest summary (2-3 lines) + top 3
              focus areas + "Book Your Next Focus Session". A "View full
              history" toggle reveals the timeline, per-skill notes, and
              "Bring a Training Partner" prompt. Everything else moves to
              Advanced view to keep this default clean and uncluttered. */}
          <ProgressGlance studentId={user.id} />
        </>
      ) : (
        <>
          {/* Advanced view — Performance Athlete Card + milestones + clickable radar + progression + synergy + multi-coach + history */}
          <MilestonesRoadmap studentId={user.id} sport={sport} />

          <PerformanceAthleteCard studentId={user.id} sport={sport} />

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Wrap radar in a clickable overlay that opens the expand modal */}
            <div className="relative group">
              <button
                type="button"
                onClick={() => setRadarOpen(true)}
                data-testid="radar-expand-trigger"
                className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-wider px-2 py-1 border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1 bg-[#0A0A0A]"
                title="Expand for Strengths vs Focus Areas"
              >
                <Maximize2 className="w-3 h-3" /> Expand
              </button>
              <SkillSnapshotRadar
                studentId={user.id}
                color={meta.color}
                title={`Latest ${meta.label.toLowerCase()} skill snapshot`}
              />
            </div>
            <div className="card-surface p-6 fade-up d2">
              <div className="label-mono mb-4">{meta.label} progression over time</div>
              <div className="h-80">
                {lineData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={lineData}>
                      <XAxis dataKey="date" tick={{ fill: "#A3A3A3", fontSize: 10 }} stroke="#262626" />
                      <YAxis domain={[0, 10]} tick={{ fill: "#A3A3A3", fontSize: 10 }} stroke="#262626" />
                      <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626" }} />
                      <Line type="monotone" dataKey="avg" stroke={meta.color} strokeWidth={2} dot={{ fill: meta.color, r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="flex h-full items-center justify-center text-sm text-[#A3A3A3]">No ratings yet.</div>}
              </div>
            </div>
          </div>

          {multiSport && synergy && (
            <div className="card-surface p-6 fade-up d3 border-l-2 border-l-[#CCFF00]" data-testid="synergy-panel">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#CCFF00]" />
                <div className="label-mono text-[#CCFF00]">CROSS-SPORT SYNERGY · FUNDAMENTALS</div>
              </div>
              <h3 className="h-display text-2xl mb-1 tracking-tight">Skills that transfer between Squash &amp; Pickleball</h3>
              <p className="text-sm text-[#A3A3A3] mb-6 max-w-2xl">
                Footwork, volley control, mental and fitness are fundamental across all racket sports.
                {synergy.focus_skill && (
                  <> Your weakest shared skill right now is <span className="text-[#FF4500] font-semibold">{synergy.focus_skill}</span> — improving it lifts both sports.</>
                )}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(synergy.shared_skills || []).map((s) => {
                  const combined = synergy.combined?.[s] || 0;
                  const isFocus = s === synergy.focus_skill;
                  return (
                    <div key={s} className="border p-4" style={{ borderColor: isFocus ? "#FF4500" : "#262626" }}>
                      <div className="label-mono mb-2" style={isFocus ? { color: "#FF4500" } : {}}>{s}</div>
                      <div className="h-display text-3xl tracking-tighter mb-3" style={{ color: isFocus ? "#FF4500" : "#CCFF00" }}>
                        {combined || "—"}<span className="text-sm text-[#A3A3A3]">/10</span>
                      </div>
                      <div className="space-y-1">
                        {(synergy.active_sports || []).map((sp) => (
                          <div key={sp} className="flex items-center justify-between text-xs">
                            <span className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5" style={{ background: SPORT_META[sp].color }} />
                              {SPORT_META[sp].label}
                            </span>
                            <span className="font-mono text-[#A3A3A3]">{synergy.by_sport?.[sp]?.[s] || 0}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {byCoach?.primary && byCoach.others?.length > 0 && (
            <div className="card-surface p-6 fade-up d2" data-testid="multi-coach-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#CCFF00]" />
                  <div className="label-mono">{meta.label} · Coach views</div>
                </div>
                {byCoach.others.length > 0 && (
                  <button
                    onClick={() => setShowOthers((s) => !s)}
                    className="text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] px-2 py-1 inline-flex items-center gap-1"
                    data-testid="multi-coach-toggle"
                  >
                    {showOthers ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {showOthers ? "Hide" : `Compare with ${byCoach.others.length} other coach${byCoach.others.length === 1 ? "" : "es"}`}
                  </button>
                )}
              </div>
              <div className="mb-2 text-xs text-[#A3A3A3]">
                Your <span className="text-[#CCFF00]">current rating</span> below comes from your most-recent session. Tap to compare with other coaches who&apos;ve rated you.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#A3A3A3] text-xs uppercase tracking-wider">
                      <th className="py-2 pr-4">Coach</th>
                      {(byCoach.skills || skills).map((s) => <th key={s} className="py-2 pr-2 text-center">{s}</th>)}
                      <th className="py-2 pr-2 text-right">Rated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-[#262626]" style={{ background: "rgba(204,255,0,0.04)" }} data-testid="primary-coach-row">
                      <td className="py-2 pr-4">
                        <span className="text-[#CCFF00] font-semibold">{byCoach.primary.coach_name}</span>
                        <span className="ml-2 text-[9px] text-[#525252] uppercase">primary</span>
                      </td>
                      {(byCoach.skills || skills).map((s) => (
                        <td key={s} className="py-2 pr-2 text-center font-mono text-[#CCFF00]">
                          {byCoach.primary.ratings?.[s] ?? "—"}
                        </td>
                      ))}
                      <td className="py-2 pr-2 text-right text-[#A3A3A3] text-xs">{(byCoach.primary.created_at || "").slice(0, 10)}</td>
                    </tr>
                    {showOthers && (byCoach.others || []).map((o) => (
                      <tr key={o.coach_id} className="border-t border-[#262626]" data-testid={`other-coach-row-${o.coach_id}`}>
                        <td className="py-2 pr-4 text-[#E5E5E5]">{o.coach_name}</td>
                        {(byCoach.skills || skills).map((s) => {
                          const primaryVal = byCoach.primary.ratings?.[s];
                          const v = o.ratings?.[s];
                          const diff = (typeof v === "number" && typeof primaryVal === "number") ? v - primaryVal : 0;
                          const color = diff > 0 ? "#CCFF00" : diff < 0 ? "#FF4500" : "#A3A3A3";
                          return (
                            <td key={s} className="py-2 pr-2 text-center font-mono" style={{ color }} title={`Primary: ${primaryVal ?? "—"}`}>
                              {v ?? "—"}
                              {diff !== 0 && <span className="text-[8px] ml-0.5">{diff > 0 ? `+${diff}` : diff}</span>}
                            </td>
                          );
                        })}
                        <td className="py-2 pr-2 text-right text-[#A3A3A3] text-xs">{(o.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card-surface p-6 fade-up d3">
            <div className="label-mono mb-4">{meta.label} rating history ({history.length})</div>
            {history.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#A3A3A3] text-xs uppercase tracking-wider">
                      <th className="py-2 pr-4">Date</th>
                      {skills.map((s) => <th key={s} className="py-2 pr-2 text-center">{s}</th>)}
                      <th className="py-2 text-left">Focus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice().reverse().map((h) => (
                      <tr key={h.id} className="border-t border-[#262626]">
                        <td className="py-2 pr-4 text-[#A3A3A3]">{(h.created_at || "").slice(0, 10)}</td>
                        {skills.map((s) => <td key={s} className="py-2 pr-2 text-center font-mono">{h.ratings?.[s] ?? "—"}</td>)}
                        <td className="py-2 text-[#E5E5E5]">{h.session_focus || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-sm text-[#A3A3A3]">No {meta.label.toLowerCase()} sessions rated yet.</div>}
          </div>
        </>
      )}

      {/* Iter158/159 — Encouragement + spider timeline live in the ADVANCED
          view only now, to keep the Simple glance clean. Simple view has
          its own inline "View full history" toggle inside ProgressGlance. */}
      {view === "advanced" && (
        <>
          <EncouragementCard studentId={user.id} sport={sport} />
          {user?.id && <StudentSpiderTimeline studentId={user.id} />}
          {/* Iter168 — Purchase + credit + feedback in one place, always
              visible in Advanced view so the student sees exactly what
              they've bought, used, and got back from each session. */}
          {user?.id && <PurchaseHistory studentId={user.id} />}
          <AchievementBadges studentId={user.id} sport={sport} />
        </>
      )}

      {/* Radar expand modal (Advanced view only) */}
      <RadarExpandModal
        studentId={user.id}
        open={radarOpen}
        onOpenChange={setRadarOpen}
        color={meta.color}
      />
    </div>
  );
}

function ViewToggle({ view, onChange }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="progress-view-toggle">
      <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] mr-1">View:</div>
      <button
        type="button"
        onClick={() => onChange("simple")}
        data-testid="view-simple"
        className="text-sm font-bold uppercase tracking-wider px-4 py-2 border transition-colors inline-flex items-center gap-2"
        style={{
          background: view === "simple" ? "#CCFF00" : "transparent",
          color: view === "simple" ? "#0A0A0A" : "#A3A3A3",
          borderColor: view === "simple" ? "#CCFF00" : "#262626",
        }}
      >
        <ListChecks className="w-4 h-4" /> Simple roadmap
      </button>
      <button
        type="button"
        onClick={() => onChange("advanced")}
        data-testid="view-advanced"
        className="text-sm font-bold uppercase tracking-wider px-4 py-2 border transition-colors inline-flex items-center gap-2"
        style={{
          background: view === "advanced" ? "#66C2FF" : "transparent",
          color: view === "advanced" ? "#0A0A0A" : "#A3A3A3",
          borderColor: view === "advanced" ? "#66C2FF" : "#262626",
        }}
      >
        <BarChart3 className="w-4 h-4" /> Advanced analytics
      </button>
    </div>
  );
}
